/* eslint-disable */
import { createElement, FC, ReactNode } from 'react';

interface TableProps {
  as?: string;
  wrapperClassName?: string;
  className?: string;
  isError?: boolean;
  isLoading?: boolean;
  error?: any;
  dataCount?: number;
  children?: ReactNode;
}

interface TableSectionProps {
  as?: string;
  className?: string;
  children?: ReactNode;
  [key: string]: any;
}

interface LoadingProps {
  isLoading?: boolean;
}

interface ErrorProps {
  isError?: boolean;
  error?: any;
}

interface TableComponent extends FC<TableProps> {
  Thead: FC<TableSectionProps>;
  Th: FC<TableSectionProps>;
  Tbody: FC<TableSectionProps>;
  Tr: FC<TableSectionProps>;
  Td: FC<TableSectionProps>;
  Loading: FC<LoadingProps>;
  Error: FC<ErrorProps>;
  NoData: FC;
}

const Table: TableComponent = ({
  as = 'table',
  wrapperClassName = '',
  className = '',
  isError = false,
  isLoading = false,
  error = null,
  dataCount = 0,
  children,
  ...props
}) => {
  return (
    <div className={`overflow-x-auto h-full ${wrapperClassName}`}>
      <div className="inline-block min-w-full">
        {createElement(as, {
          className: `min-w-full ${className}`,
          ...props,
          children,
        })}
      </div>
      {/** @ts-ignore */}
      <Table.Loading isLoading={isLoading} />
      {/** @ts-ignore */}
      <Table.Error isError={isError} error={error ?? null} />
      {/** @ts-ignore */}
      {!isLoading && !isError && dataCount < 1 && <Table.NoData />}
    </div>
  );
};

Table.Thead = ({ as = 'thead', className = '', children, ...props }: TableSectionProps) =>
  createElement(as, {
    className: `${className}`,
    ...props,
    children,
  });

Table.Th = ({ as = 'th', className = '', children = '', ...props }: TableSectionProps) =>
  createElement(as, {
    ...props,
    className: `whitespace-nowrap px-3 py-2 text-left text-sm font-semibold text-gray-900 ${className}`,
    children,
  });

Table.Tbody = ({ as = 'tbody', className = '', children, ...props }: TableSectionProps) =>
  createElement(as, {
    className: `bg-white ${className}`,
    ...props,
    children,
  });

Table.Tr = ({ as = 'tr', className = '', children, ...props }: TableSectionProps) =>
  createElement(as, {
    className: `${className}`,
    ...props,
    children,
  });

Table.Td = ({ as = 'td', className = '', children, ...props }: TableSectionProps) =>
  createElement(as, {
    className: `whitespace-nowrap px-3 py-2 text-sm text-black ${className}`,
    ...props,
    children,
  });

Table.Loading = ({ isLoading = false }: LoadingProps) => {
  return isLoading ? (
    <div className="flex min-h-[100px] w-full items-center justify-center bg-white">
      <div className="text-center text-slate-500">
        <span className="mx-auto h-6 w-6 animate-spin border-2 border-gray-300 border-t-blue-500 rounded-full block" />
        <p className="mt-2">Loading...</p>
      </div>
    </div>
  ) : null;
};

Table.Error = ({ isError = false, error }: ErrorProps) => {
  return isError ? (
    <div className="flex min-h-[100px] items-center justify-center bg-white">
      <div className="text-center text-red-500">
        <span className="mx-auto h-6 w-6 block">!</span>
        <p className="mt-2">{error?.message || 'Error loading data.'}</p>
      </div>
    </div>
  ) : null;
};

Table.NoData = () => {
  return (
    <div className="flex min-h-[100px] items-center justify-center bg-white">
      <div className="text-center text-gray-400">
        <span className="mx-auto h-6 w-6 block">—</span>
        <p className="mt-2">No data to show.</p>
      </div>
    </div>
  );
};

export default Table;
